/**
 * Enterprise API Client
 *
 * Provides typed access to entities, functions, integrations, and auth.
 *
 * Usage:
 *   import { api } from "@/api";
 *   const clients = await api.entities.Client.list();
 *   await api.functions.invoke('calculateFinancialHealth', { clientId });
 *   await api.integrations.InvokeLLM({ prompt });
 *   const user = await api.auth.me();
 */

import { apiClient } from './client';

// ────────────────────────────────────────────────────
// Entity endpoint registry
// ────────────────────────────────────────────────────

const ENTITY_ENDPOINTS: Record<string, string> = {
  // Core
  Client: '/clients',
  Task: '/tasks',
  Goal: '/goals',
  Appointment: '/appointments',
  Document: '/documents',
  Note: '/notes',
  Notification: '/notifications',
  Household: '/households',
  Communication: '/communications',
  ActivityLog: '/activity-logs',

  // Client sub-resources
  ClientFinancialData: '/client-financial-data',
  ClientPlanningProfile: '/client-planning-profiles',
  ClientConsolidatedScores: '/client-consolidated-scores',
  ClientCustomMetric: '/client-custom-metrics',
  ClientDataScoring: '/client-data-scoring',
  ClientInvitation: '/client-invitations',
  ClientOnboarding: '/client-onboarding',
  ClientOperationalStatus: '/client-operational-status',
  ClientSegment: '/client-segments',
  ObjectSegment: '/object-segments',
  ObjectSegmentMember: '/object-segment-members',
  ClientSnapshot: '/client-snapshots',
  ClientTaxProfile: '/client-tax-profiles',
  ClientTaxStrategy: '/client-tax-strategies',
  ClientWhatIfScenario: '/client-what-if-scenarios',
  ClientAdvisorMessage: '/client-advisor-messages',

  // Planning & Financial
  AdvancedRetirementAnalysis: '/advanced-retirement-analysis',
  RetirementProjection: '/retirement-projections',
  DebtAccount: '/debt-accounts',
  DebtPayoffScenario: '/debt-payoff-scenarios',
  SavingsScenario: '/savings-scenarios',
  TaxAnalysis: '/tax-analysis',
  TaxScenario: '/tax-scenarios',
  NetWorthSnapshot: '/net-worth-snapshots',
  CustodianAccount: '/custodian-accounts',
  InsurancePolicy: '/insurance-policies',
  Loan: '/loans',
  InvestmentModel: '/investment-models',

  // Cases & Workflows
  Case: '/cases',
  CaseItem: '/case-items',
  CaseTemplate: '/case-templates',
  Workflow: '/workflows',
  WorkflowDefinition: '/workflow-definitions',
  WorkflowTemplate: '/workflow-templates',
  WorkflowInstance: '/workflow-instances',
  WorkflowExecution: '/workflow-executions',
  WorkflowAction: '/workflow-actions',
  InternalWorkflowTemplate: '/internal-workflow-templates',
  Taskflow: '/taskflows',

  // Tasks & Automation
  TaskTemplate: '/task-templates',
  TaskType: '/task-types',
  Automation: '/automations',
  AutomationExecution: '/automation-executions',
  Action: '/actions',
  ActionTemplate: '/action-templates',

  // Forms & Questionnaires
  Form: '/forms',
  FormVersion: '/form-versions',
  FormAssignment: '/form-assignments',
  Questionnaire: '/questionnaires',
  QuestionnaireVersion: '/questionnaire-versions',
  Response: '/responses',

  // Documents
  DocumentTemplate: '/document-templates',
  DocumentTemplateFolder: '/document-template-folders',
  DocumentSigningRequest: '/document-signing-requests',
  DocumentActivity: '/document-activities',
  VaultDocument: '/vault-documents',
  Folder: '/folders',
  GlobalDocumentFolder: '/global-document-folders',

  // Reviews
  ReviewSession: '/review-sessions',
  ReviewTemplate: '/review-templates',

  // Campaigns & Marketing
  Campaign: '/campaigns',
  CampaignActivity: '/campaign-activities',
  CampaignEnrollment: '/campaign-enrollments',
  CampaignAssetLink: '/campaign-asset-links',
  EmailTemplate: '/email-templates',
  SmsTemplate: '/sms-templates',

  // Calendar
  Availability: '/availability',
  CalendarSettings: '/calendar-settings',
  AppointmentTemplate: '/appointment-templates',

  // Compliance
  ComplianceRecommendation: '/compliance-recommendations',
  AuditEvent: '/audit-events',
  ExceptionCase: '/exception-cases',
  RuleDefinition: '/rule-definitions',

  // Users & Roles
  User: '/users',
  Role: '/roles',
  Team: '/teams',
  TeamNote: '/team-notes',
  TrustedContact: '/trusted-contacts',

  // Organizational Hierarchy
  OrgTier: '/org-tiers',
  OrgNode: '/org-nodes',
  OrgNodeMember: '/org-node-members',
  OrgNodeClient: '/org-node-clients',

  // Settings & Configuration
  GlobalSettings: '/global-settings',
  IntegrationSettings: '/integration-settings',
  CustomClientField: '/custom-client-fields',
  CustomClientFieldValue: '/custom-client-field-values',
  CustomDataFieldTemplate: '/custom-data-field-templates',
  DataTag: '/data-tags',
  ScoringTemplate: '/scoring-templates',
  ScoringMetric: '/scoring-metrics',
  ScoringMetricQuestion: '/scoring-metric-questions',
  UserPreferences: '/user-preferences',
  UserDashboard: '/user-dashboards',
  UserNotificationSetting: '/user-notification-settings',
  CustomWidget: '/custom-widgets',
  QuickActionConfig: '/quick-action-configs',
  OliPersonalization: '/oli-personalization',

  // Goals
  GoalTemplate: '/goal-templates',

  // Other
  Query: '/queries',
  Recommendation: '/recommendations',
  AdvisorPlan: '/advisor-plans',
  ApplicationRelease: '/application-releases',
  Lead: '/leads',
  Pipeline: '/pipelines',
  LeadPipelineStage: '/lead-pipeline-stages',
  Location: '/locations',
  MarketplacePackage: '/marketplace-packages',
  OnboardingTemplate: '/onboarding-templates',
  PackageDownload: '/package-downloads',
  PackagePurchase: '/package-purchases',
  PlaidItem: '/plaid-items',
  ExportJob: '/export-jobs',
  MergeLog: '/merge-logs',
  FormResponse: '/form-responses',
};

// ────────────────────────────────────────────────────
// Entity proxy — dynamic CRUD for any model
// ────────────────────────────────────────────────────

/**
 * Build query string from filter object, sort, and limit.
 * Handles complex operators ($in, $gte, $lte, $gt, $lt, $or, $and, $exists, $regex)
 * by JSON-encoding object values so the backend can parse them.
 */
function buildQueryString(filter?: Record<string, any>, sort?: string, limit?: number): string {
  const params = new URLSearchParams();
  if (filter) {
    Object.entries(filter).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (typeof value === 'object') {
        // JSON-encode complex filter values ($in, $gte, arrays, etc.)
        params.set(key, JSON.stringify(value));
      } else {
        params.set(key, String(value));
      }
    });
  }
  if (sort) params.set('sort', sort);
  if (limit != null && limit > 0) params.set('limit', String(limit));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

function createEntityAccessor(entityName: string) {
  const endpoint = ENTITY_ENDPOINTS[entityName] || `/${entityName.toLowerCase()}s`;

  return {
    /**
     * List entities with optional sort and limit.
     * Matches base44 signature: list(sort?, limit?)
     */
    async list(sort?: string, limit?: number) {
      const params = new URLSearchParams();
      if (sort) params.set('sort', sort);
      if (limit != null && limit > 0) params.set('limit', String(limit));
      const qs = params.toString();
      const url = qs ? `${endpoint}?${qs}` : endpoint;
      const res = await apiClient.get(url);
      return res.data?.data ?? res.data ?? [];
    },

    /**
     * Filter entities with query object, sort, and limit.
     * Matches base44 signature: filter(query?, sort?, limit?)
     * Supports MongoDB-style operators: $in, $gte, $lte, $gt, $lt, $exists, $regex, $or, $and
     */
    async filter(query?: Record<string, any>, sort?: string, limit?: number) {
      const qs = buildQueryString(query, sort, limit);
      const res = await apiClient.get(`${endpoint}${qs}`);
      return res.data?.data ?? res.data ?? [];
    },

    async get(id: string | number) {
      const res = await apiClient.get(`${endpoint}/${id}`);
      return res.data?.data ?? res.data;
    },

    async create(data: Record<string, any>) {
      const res = await apiClient.post(endpoint, data);
      return res.data?.data ?? res.data;
    },

    async update(id: string | number, data: Record<string, any>) {
      const res = await apiClient.put(`${endpoint}/${id}`, data);
      return res.data?.data ?? res.data;
    },

    async delete(id: string | number) {
      const res = await apiClient.delete(`${endpoint}/${id}`);
      return res.data?.data ?? res.data;
    },

    /**
     * Bulk create multiple entities at once.
     * Matches base44 signature: bulkCreate(items[])
     */
    async bulkCreate(items: Record<string, any>[]) {
      const res = await apiClient.post(`${endpoint}/bulk`, { items });
      return res.data?.data ?? res.data ?? [];
    },

    async schema() {
      try {
        const res = await apiClient.get(`${endpoint}/schema`);
        return res.data?.data ?? res.data;
      } catch {
        return { properties: {} };
      }
    },
  };
}

const entitiesProxy = new Proxy({} as Record<string, ReturnType<typeof createEntityAccessor>>, {
  get(_target, prop: string) {
    if (typeof prop === 'string') return createEntityAccessor(prop);
    return undefined;
  },
});

// ────────────────────────────────────────────────────
// Functions — invoke backend functions by name
// ────────────────────────────────────────────────────

const functions = {
  async invoke(functionName: string, params?: Record<string, any>, queryString?: string) {
    const qs = queryString || '';
    const res = await apiClient.post(`/functions/${functionName}${qs}`, params || {});
    const body = res.data?.data ?? res.data;
    return { data: body };
  },
};

// ────────────────────────────────────────────────────
// SMS Assets — lifecycle-managed SMS messages
// ────────────────────────────────────────────────────

const smsAssets = {
  async list(query?: Record<string, any>) {
    const res = await apiClient.get('/sms-assets', { params: query });
    return res.data;
  },
  async get(id: string) {
    const res = await apiClient.get(`/sms-assets/${id}`);
    return res.data?.data ?? res.data;
  },
  async create(data: Record<string, any>) {
    const res = await apiClient.post('/sms-assets', data);
    return res.data?.data ?? res.data;
  },
  async update(id: string, data: Record<string, any>) {
    const res = await apiClient.put(`/sms-assets/${id}`, data);
    return res.data?.data ?? res.data;
  },
  async delete(id: string) {
    const res = await apiClient.delete(`/sms-assets/${id}`);
    return res.data;
  },
  async markSent(id: string) {
    const res = await apiClient.post(`/sms-assets/${id}/send`);
    return res.data?.data ?? res.data;
  },
  async archive(id: string) {
    const res = await apiClient.post(`/sms-assets/${id}/archive`);
    return res.data?.data ?? res.data;
  },
  async restore(id: string) {
    const res = await apiClient.post(`/sms-assets/${id}/restore`);
    return res.data?.data ?? res.data;
  },
  async clone(id: string) {
    const res = await apiClient.post(`/sms-assets/${id}/clone`);
    return res.data?.data ?? res.data;
  },
};

// ────────────────────────────────────────────────────
// Email Assets — lifecycle-managed email messages
// ────────────────────────────────────────────────────

const emailAssets = {
  async list(query?: Record<string, any>) {
    const res = await apiClient.get('/email-assets', { params: query });
    return res.data;
  },
  async get(id: string) {
    const res = await apiClient.get(`/email-assets/${id}`);
    return res.data?.data ?? res.data;
  },
  async create(data: Record<string, any>) {
    const res = await apiClient.post('/email-assets', data);
    return res.data?.data ?? res.data;
  },
  async update(id: string, data: Record<string, any>) {
    const res = await apiClient.put(`/email-assets/${id}`, data);
    return res.data?.data ?? res.data;
  },
  async delete(id: string) {
    const res = await apiClient.delete(`/email-assets/${id}`);
    return res.data;
  },
  async markSent(id: string) {
    const res = await apiClient.post(`/email-assets/${id}/send`);
    return res.data?.data ?? res.data;
  },
  async archive(id: string) {
    const res = await apiClient.post(`/email-assets/${id}/archive`);
    return res.data?.data ?? res.data;
  },
  async restore(id: string) {
    const res = await apiClient.post(`/email-assets/${id}/restore`);
    return res.data?.data ?? res.data;
  },
  async clone(id: string) {
    const res = await apiClient.post(`/email-assets/${id}/clone`);
    return res.data?.data ?? res.data;
  },
};

// ────────────────────────────────────────────────────
// Integrations — LLM, email, SMS, file upload, etc.
// ────────────────────────────────────────────────────

const integrations = {
  Core: {
    async InvokeLLM(params: { prompt: string; model?: string; response_json_schema?: any; [key: string]: any }) {
      const res = await apiClient.post('/integrations/llm', params);
      return res.data?.data ?? res.data;
    },
    /**
     * Async LLM call — starts the job then polls until result is ready.
     * Avoids 504 gateway timeout for long-running LLM calls (>60s).
     */
    async InvokeLLMAsync(params: { prompt: string; model?: string; response_json_schema?: any; [key: string]: any }, options?: { pollInterval?: number; maxWait?: number }) {
      const pollInterval = options?.pollInterval || 3000;
      const maxWait = options?.maxWait || 300000; // 5 min max

      // Start the async job
      const startRes = await apiClient.post('/integrations/llm/async', params);
      const jobId = startRes.data?.jobId;
      if (!jobId) throw new Error('Failed to start async LLM job');

      // Poll for result
      const deadline = Date.now() + maxWait;
      while (Date.now() < deadline) {
        await new Promise(r => setTimeout(r, pollInterval));
        const pollRes = await apiClient.get(`/integrations/llm/async/${jobId}`);
        const result = pollRes.data;

        if (result.status === 'completed') {
          return result.data;
        }
        if (result.status === 'failed') {
          throw new Error(result.error || 'LLM job failed');
        }
        // status === 'processing' → continue polling
      }

      throw new Error('LLM job timed out after ' + Math.round(maxWait / 1000) + 's');
    },
    async SendEmail(params: { to: string; subject: string; body: string; [key: string]: any }) {
      const res = await apiClient.post('/integrations/email', params);
      return res.data?.data ?? res.data;
    },
    async SendSMS(params: { to: string; message: string; [key: string]: any }) {
      const res = await apiClient.post('/integrations/sms', params);
      return res.data?.data ?? res.data;
    },
    async UploadFile(fileOrObj: File | Blob | { file: File | Blob }, options?: Record<string, any>) {
      // Handle both base44 style { file } wrapper and raw File
      const actualFile = (fileOrObj && typeof fileOrObj === 'object' && 'file' in fileOrObj && !(fileOrObj instanceof Blob)) ? fileOrObj.file : fileOrObj as File | Blob;
      const formData = new FormData();
      formData.append('file', actualFile);
      if (options) {
        Object.entries(options).forEach(([key, value]) => formData.append(key, String(value)));
      }
      const res = await apiClient.post('/integrations/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return res.data?.data ?? res.data;
    },
    async GenerateImage(params: { prompt: string; [key: string]: any }) {
      const res = await apiClient.post('/integrations/generate-image', params);
      return res.data?.data ?? res.data;
    },
    async ExtractDataFromUploadedFile(params: { file_uri: string; [key: string]: any }) {
      const res = await apiClient.post('/integrations/extract-data', params);
      return res.data?.data ?? res.data;
    },
    async CreateFileSignedUrl(params: { file_uri: string; [key: string]: any }) {
      const res = await apiClient.post('/integrations/signed-url', params);
      return res.data?.data ?? res.data;
    },
    async UploadPrivateFile(fileOrObj: File | Blob | { file: File | Blob }, options?: Record<string, any>) {
      // Handle both base44 style { file } wrapper and raw File
      const actualFile = (fileOrObj && typeof fileOrObj === 'object' && 'file' in fileOrObj && !(fileOrObj instanceof Blob)) ? fileOrObj.file : fileOrObj as File | Blob;
      const formData = new FormData();
      formData.append('file', actualFile);
      if (options) {
        Object.entries(options).forEach(([key, value]) => formData.append(key, String(value)));
      }
      const res = await apiClient.post('/integrations/upload-private', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return res.data?.data ?? res.data;
    },
  },
};

// ────────────────────────────────────────────────────
// Auth — current user, login, logout
// ────────────────────────────────────────────────────

const auth = {
  async me() {
    try {
      const res = await apiClient.get('/auth/me');
      return res.data?.user ?? res.data;
    } catch {
      return null;
    }
  },

  redirectToLogin() {
    window.dispatchEvent(new Event('auth:logout'));
  },

  async logout() {
    try {
      await apiClient.post('/auth/logout');
    } catch {
      // ignore
    }
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    window.dispatchEvent(new Event('auth:logout'));
  },

  async updateMe(data: Record<string, any>) {
    const res = await apiClient.put('/auth/me', data);
    return res.data?.user ?? res.data;
  },
};

// ────────────────────────────────────────────────────
// Org Hierarchy API (custom tree endpoints)
// ────────────────────────────────────────────────────

const orgHierarchy = {
  // Tiers
  listTiers: () => apiClient.get('/org-hierarchy/tiers').then(r => r.data),
  createTier: (data: any) => apiClient.post('/org-hierarchy/tiers', data).then(r => r.data),
  bulkCreateTiers: (tiers: { name: string; icon?: string }[]) =>
    apiClient.post('/org-hierarchy/tiers/bulk', { tiers }).then(r => r.data),
  updateTier: (id: string, data: any) => apiClient.put(`/org-hierarchy/tiers/${id}`, data).then(r => r.data),
  deleteTier: (id: string) => apiClient.delete(`/org-hierarchy/tiers/${id}`).then(r => r.data),
  reorderTiers: (tiers: { id: string; level: number }[]) =>
    apiClient.post('/org-hierarchy/tiers/reorder', { tiers }).then(r => r.data),

  // Tree & Nodes
  getTree: () => apiClient.get('/org-hierarchy/tree').then(r => r.data),
  listNodes: (params?: any) => apiClient.get('/org-hierarchy/nodes', { params }).then(r => r.data),
  createNode: (data: any) => apiClient.post('/org-hierarchy/nodes', data).then(r => r.data),
  updateNode: (id: string, data: any) => apiClient.put(`/org-hierarchy/nodes/${id}`, data).then(r => r.data),
  deleteNode: (id: string) => apiClient.delete(`/org-hierarchy/nodes/${id}`).then(r => r.data),
  moveNode: (id: string, parentId: string | null) =>
    apiClient.post(`/org-hierarchy/nodes/${id}/move`, { parentId }).then(r => r.data),

  // Members
  listMembers: (nodeId: string) =>
    apiClient.get(`/org-hierarchy/nodes/${nodeId}/members`).then(r => r.data),
  assignMembers: (nodeId: string, userIds: string[], role?: string, force?: boolean) =>
    apiClient.post(`/org-hierarchy/nodes/${nodeId}/members`, { userIds, role, force }).then(r => r.data),
  removeMember: (nodeId: string, userId: string) =>
    apiClient.delete(`/org-hierarchy/nodes/${nodeId}/members/${userId}`).then(r => r.data),

  // Client Scoping
  listClients: (nodeId: string) =>
    apiClient.get(`/org-hierarchy/nodes/${nodeId}/clients`).then(r => r.data),
  listOrgClients: () =>
    apiClient.get('/org-hierarchy/clients').then(r => r.data),
  getNodePermissions: (nodeId: string) =>
    apiClient.get(`/org-hierarchy/nodes/${nodeId}/permissions`).then(r => r.data),
  assignClients: (nodeId: string, clientIds: string[]) =>
    apiClient.post(`/org-hierarchy/nodes/${nodeId}/clients`, { clientIds }).then(r => r.data),
  removeClient: (nodeId: string, clientId: string) =>
    apiClient.delete(`/org-hierarchy/nodes/${nodeId}/clients/${clientId}`).then(r => r.data),

  // User Scope
  getUserScope: (userId: string) =>
    apiClient.get(`/org-hierarchy/user/${userId}/scope`).then(r => r.data),
};

// ────────────────────────────────────────────────────
// Users — permission management
// ────────────────────────────────────────────────────

const users = {
  /** Fetch full permission breakdown for a user (user, role, team, effective, defaults) */
  async getPermissions(userId: string) {
    const res = await apiClient.get(`/users/${userId}/permissions`);
    return res.data;
  },
};

// ────────────────────────────────────────────────────
// Clients — direct operations
// ────────────────────────────────────────────────────

const clients = {
  inviteToPortal: (clientId: string, message?: string) =>
    apiClient.post(`/clients/${clientId}/invite`, { message }).then(r => r.data),
};

// ────────────────────────────────────────────────────
// Exported API object
// ────────────────────────────────────────────────────

export const api = {
  entities: entitiesProxy,
  functions,
  integrations,
  smsAssets,
  emailAssets,
  auth,
  orgHierarchy,
  users,
  clients,
};

export default api;
